import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { listEvents, type EventQuery } from '../../api/eventsApi';
import type { EventListItem } from '../../api/types';
import { Pager } from '../../components/Pager';
import './trading.css';

// 권위: data/reports/UNIMPLEMENTED_INVENTORY.md §4.3 — Trading Event Audit.
//   TRADING.* / LLM.GATE_B.* / BROKER.API.* 도메인의 이벤트 페이지네이션.
//   기존 /api/v1/events (EventController) 를 그대로 사용하므로 dashboard-api 신설 endpoint 불필요.
//   필터: prefix (event_code) + severity. signal_id (entityRefs) 클릭 시 cascade trail —
//   본 화면은 1차로 prefix 필터 + 상세 navigate 만 제공 (cascade trail 은 화면 4 AgentHistory 가 담당).

type DomainFilter = 'TRADING' | 'LLM.GATE_B' | 'BROKER.API' | 'ALL';

const DOMAIN_PREFIX: Record<DomainFilter, string> = {
  TRADING: 'TRADING.',
  'LLM.GATE_B': 'LLM.GATE_B.',
  'BROKER.API': 'BROKER.API.',
  ALL: '',
};

const SEVERITY_OPTIONS = ['ALL', 'FATAL', 'ERROR', 'WARN', 'INFO'] as const;
type SeverityFilter = (typeof SEVERITY_OPTIONS)[number];

export default function TradingEventStreamPage(): JSX.Element {
  const [domain, setDomain] = useState<DomainFilter>('TRADING');
  const [severity, setSeverity] = useState<SeverityFilter>('ALL');
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const query: EventQuery = useMemo(
    () => ({
      eventCode: DOMAIN_PREFIX[domain] || undefined,
      severity: severity === 'ALL' ? undefined : severity,
      page,
      pageSize,
    }),
    [domain, severity, page],
  );

  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['trading-events', query],
    queryFn: () => listEvents(query),
    staleTime: 30_000,
  });

  const items: EventListItem[] = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="trading-page">
      <header className="trading-page__header">
        <h1 className="trading-page__title">트레이딩 이벤트 audit</h1>
        <span className="trading-page__subtitle">TRADING.* · LLM.GATE_B.* · BROKER.API.*</span>
      </header>

      <div className="trading-filter" role="group" aria-label="이벤트 필터">
        <label className="trading-filter__field">
          <span>도메인</span>
          <select
            value={domain}
            onChange={(e) => {
              setDomain(e.target.value as DomainFilter);
              setPage(0);
            }}
          >
            <option value="TRADING">TRADING.*</option>
            <option value="LLM.GATE_B">LLM.GATE_B.*</option>
            <option value="BROKER.API">BROKER.API.*</option>
            <option value="ALL">전체</option>
          </select>
        </label>
        <label className="trading-filter__field">
          <span>Severity</span>
          <select
            value={severity}
            onChange={(e) => {
              setSeverity(e.target.value as SeverityFilter);
              setPage(0);
            }}
          >
            {SEVERITY_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <span className="trading-page__subtitle" style={{ marginLeft: 'auto' }}>
          {total.toLocaleString()}건 · {page + 1} / {totalPages}
        </span>
      </div>

      <section className="trading-card" aria-label="이벤트 목록">
        <header className="trading-card__header">
          <h2 className="trading-card__title">이벤트</h2>
        </header>
        <div className="trading-card__body">
          {isLoading && <div className="trading-table__empty">불러오는 중…</div>}
          {isError && (
            <div className="trading-page__notice" role="alert">
              <strong>오류 —</strong>
              {(error as Error)?.message ?? '이벤트 조회 실패'}
            </div>
          )}
          {!isLoading && !isError && (
            <table className="trading-table">
              <thead>
                <tr>
                  <th>시각</th>
                  <th>Severity</th>
                  <th>소스</th>
                  <th>EventCode</th>
                  <th>요약</th>
                  <th>traceId</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="trading-table__empty">
                      조건에 해당하는 이벤트가 없습니다.
                    </td>
                  </tr>
                )}
                {items.map((ev) => (
                  <tr
                    key={ev.eventId}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/history/${ev.eventId}`)}
                  >
                    <td>
                      <code
                        style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}
                      >
                        {formatTime(ev.occurredAt)}
                      </code>
                    </td>
                    <td>
                      <span className={`trading-badge trading-badge--${severityTone(ev.severity)}`}>
                        {ev.severity}
                      </span>
                    </td>
                    <td>
                      <code
                        style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}
                      >
                        {ev.sourceAgent || '-'}
                      </code>
                    </td>
                    <td>
                      <code
                        style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}
                      >
                        {ev.eventCode}
                      </code>
                    </td>
                    <td title={ev.summary} style={{ maxWidth: '480px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ev.summary}
                    </td>
                    <td>
                      <code
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 'var(--text-xs)',
                          color: 'var(--ink-faint)',
                        }}
                      >
                        {ev.traceId ?? '-'}
                      </code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Pager
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
          />
        </div>
      </section>
    </div>
  );
}

function severityTone(
  severity: EventListItem['severity'],
): 'ok' | 'warn' | 'danger' | 'neutral' {
  switch (severity) {
    case 'FATAL':
    case 'ERROR':
      return 'danger';
    case 'WARN':
      return 'warn';
    case 'INFO':
      return 'ok';
    default:
      return 'neutral';
  }
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}
